import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  message, 
  Spin, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Checkbox, 
  Breadcrumb, 
  Tag,
  Tooltip
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { databricksService } from '../services/databricksService';
import { accountService } from '../services/accountService';
import { API_BASE_URL } from '../services/config';
import fetchWrapper from '../components/utility/fetchWrapper';
import IsEnabled from "../rolepermission";

const { Option } = Select;

const DatabricksAccountsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [isConfigureModalVisible, setIsConfigureModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [clusterLogPaths, setClusterLogPaths] = useState([]);
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const navigate = useNavigate();
  const isViewer = !IsEnabled('Accounts');
  const [hiddenClientIds, setHiddenClientIds] = useState([]);
  const [showPatToken, setShowPatToken] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [rawApiResponse, setRawApiResponse] = useState(null);
  const [showRawResponse, setShowRawResponse] = useState(false);

  const fetchDatabricksAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/accounts`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }
      
      const rawData = await response.json();
      setRawApiResponse(rawData);
      
      // Extract accounts from raw response
      const accounts = rawData.accounts || [];
      
      if (!accounts || !Array.isArray(accounts)) {
        message.warning('Received invalid account data format');
        setDatabricksAccounts([]);
        return;
      }
      
      // Use the API response data directly - no transformation needed
      const accountsWithDetails = accounts.map((account) => {
        if (!account || !account.account_id) {
          return null;
        }
        return {
          accountId: account.account_id,
          accountUrl: account.account_url,
          clientId: account.client_id,
          updatedAt: account.updated_at,
          accountName: `Databricks Account ${account.account_id}`,
          status: 'Connected',
        };
      }).filter(Boolean);
      
      setDatabricksAccounts(accountsWithDetails);
      
      if (accounts.length === 0) {
        message.info('No Databricks accounts found');
      }
    } catch (error) {
      console.error('Error fetching Databricks accounts:', error);
      const errorMessage = error.message || 'Failed to fetch Databricks accounts';
      message.error(`Failed to fetch Databricks accounts: ${errorMessage}`);
      setDatabricksAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAwsAccounts = useCallback(async () => {
    try {
      const data = await accountService.getAccounts(user.client_id);
      const mappedDataArray = [];
      for (const key in data) {
        mappedDataArray.push({ ...data[key], accountId: key });
      }
      setAwsAccounts(mappedDataArray);
    } catch (error) {
      console.error('Failed to fetch AWS accounts:', error);
    }
  }, [user.client_id]);

  useEffect(() => {
    fetchDatabricksAccounts();
    fetchAwsAccounts();
  }, [fetchDatabricksAccounts, fetchAwsAccounts]);

  const handleConfigureLogs = async (account) => {
    setSelectedAccount(account);
    form.resetFields();
    setWorkspaces([]);
    setClusterLogPaths([]);
    
    try {
      // Get stored credentials to pre-fill PAT token if available
      const storedCreds = getStoredCredentials(account.accountId);
      
      // Fetch workspaces using account_id (backend uses stored credentials)
      try {
        const workspacesData = await databricksService.getWorkspaces(account.accountId);
        setWorkspaces(workspacesData || []);
      } catch (workspaceError) {
        console.error('Failed to fetch workspaces:', workspaceError);
        message.error('Unable to fetch workspaces. Please check your account configuration.');
        setWorkspaces([]);
      }

      // Set default values
      form.setFieldsValue({
        awsAccount: awsAccounts.length > 0 ? `${awsAccounts[0].aws_account_name} (${awsAccounts[0].accountId})` : undefined,
        workspace: undefined,
        patToken: '',
        auditLogs: true,
        realtimeLogs: false,
        auditLogsPath: 's3://databricks-logs-observability/audit',
        clusterLogsPath: undefined,
      });

      setIsConfigureModalVisible(true);
    } catch (error) {
      console.error('Failed to load account configuration:', error);
      message.error('Failed to load account configuration');
    }
  };

  const handleWorkspaceChange = async (workspaceId) => {
    if (!workspaceId || !selectedAccount) return;

    const workspace = workspaces.find(w => w.workspace_id === workspaceId);
    if (!workspace) return;

    // Reset cluster log path when workspace changes
    form.setFieldsValue({ clusterLogsPath: undefined });
    setClusterLogPaths([]);

    try {
      let workspaceUrl = workspace.workspace_url || workspace.deployment_url;
      
      if (!workspaceUrl) {
        message.warning('Workspace URL not available');
        setClusterLogPaths([]);
        return;
      }

      if (!workspaceUrl.startsWith('http://') && !workspaceUrl.startsWith('https://')) {
        workspaceUrl = `https://${workspaceUrl}`;
      }

      // PAT token must be entered by user in the form
      const patToken = form.getFieldValue('patToken');
      
      if (!patToken) {
        message.warning('PAT token is required. Please enter PAT token in the form.');
        setClusterLogPaths([]);
        return;
      }

      // Fetch cluster log paths using workspace URL and PAT token
      try {
        message.loading({ content: 'Fetching cluster log paths...', key: 'fetchingPaths' });
        const clusterPaths = await databricksService.getClusterLogPaths(
          workspaceUrl,
          patToken
        );
        const paths = clusterPaths.cluster_log_paths || [];
        setClusterLogPaths(paths);
        
        if (paths.length === 0) {
          message.warning({ content: 'No cluster log paths found for this workspace', key: 'fetchingPaths' });
        } else {
          message.success({ content: `Found ${paths.length} cluster log path(s)`, key: 'fetchingPaths' });
        }
      } catch (error) {
        console.error('Failed to fetch cluster log paths:', error);
        message.error({ 
          content: `Failed to fetch cluster log paths: ${error.message || 'Please check your PAT token and workspace URL'}`, 
          key: 'fetchingPaths',
          duration: 5
        });
        setClusterLogPaths([]);
      }
    } catch (error) {
      console.error('Failed to handle workspace change:', error);
      setClusterLogPaths([]);
    }
  };

  const handleSaveConfiguration = async (values) => {
    try {
      const selectedAwsAccount = awsAccounts.find(
        acc => values.awsAccount.includes(acc.accountId)
      );
      const selectedWorkspace = workspaces.find(
        w => w.workspace_id === values.workspace
      );

      if (!selectedAwsAccount || !selectedWorkspace) {
        message.error('Please select valid AWS account and workspace');
        return;
      }

      // Validate at least one log type is selected
      if (!values.auditLogs && !values.realtimeLogs) {
        message.error('Please select at least one log type');
        return;
      }

      // Validate audit log path if audit logs is enabled
      if (values.auditLogs && !values.auditLogsPath) {
        message.error('Please enter audit log path');
        return;
      }

      // Validate cluster log path if realtime logs is enabled
      if (values.realtimeLogs && !values.clusterLogsPath) {
        message.error('Please select a cluster log path');
        return;
      }

      // Get client_id directly from backend API response
      const clientId = selectedAccount.clientId;
      
      if (!clientId) {
        message.error('Account information not found. Please refresh the page.');
        return;
      }

      // Get stored credentials for PAT token only
      const storedCreds = getStoredCredentials(selectedAccount.accountId);

      // PAT token must be entered by user (not from localStorage)
      const patToken = values.patToken;
      if (!patToken) {
        message.error('PAT token is required. Please enter PAT token.');
        return;
      }

      // Prepare payload for /databricks/onboard API
      // Note: Backend requires both paths to be in the same bucket
      // If only one log type is enabled, we derive the other path from the enabled one
      let clusterLogPath = values.clusterLogsPath || '';
      let auditLogPath = values.auditLogsPath || '';
      
      // If only one path is provided, derive the other from the same bucket
      if (values.auditLogs && !values.realtimeLogs && auditLogPath) {
        // Only audit logs enabled - derive cluster path from audit path
        const bucketMatch = auditLogPath.match(/s3:\/\/([^\/]+)/);
        if (bucketMatch) {
          clusterLogPath = `s3://${bucketMatch[1]}/cluster/`;
        }
      } else if (values.realtimeLogs && !values.auditLogs && clusterLogPath) {
        // Only cluster logs enabled - derive audit path from cluster path
        const bucketMatch = clusterLogPath.match(/s3:\/\/([^\/]+)/);
        if (bucketMatch) {
          auditLogPath = `s3://${bucketMatch[1]}/audit/`;
        }
      }
      
      const onboardPayload = {
        client_id: clientId,
        workspace_name: selectedWorkspace.workspace_name || `Workspace ${selectedWorkspace.workspace_id}`,
        token: patToken,
        aws_account_id: selectedAwsAccount.accountId,
        cluster_log_path: clusterLogPath,
        audit_log_path: auditLogPath,
      };

      // Call POST /databricks/onboard to onboard logs
      await databricksService.onboardLogs(onboardPayload);
      message.success('Log configuration saved successfully');
      setIsConfigureModalVisible(false);
      form.resetFields();
      setWorkspaces([]);
      setClusterLogPaths([]);
      
      // Dispatch custom event to notify dashboard to refresh (in case account count changes)
      window.dispatchEvent(new Event('databricksAccountsUpdated'));
    } catch (error) {
      console.error('Failed to save configuration:', error);
      message.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
    }
  };

  // Helper function to get stored credentials from localStorage
  const getStoredCredentials = (accountId) => {
    try {
      const stored = localStorage.getItem(`databricks_credentials_${accountId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  };

  // Helper function to store credentials in localStorage
  const storeCredentials = (accountId, credentials) => {
    try {
      localStorage.setItem(`databricks_credentials_${accountId}`, JSON.stringify({
        client_id: credentials.client_id,
        client_secret: credentials.client_secret,
        pat_token: credentials.pat_token,
        account_url: credentials.account_url,
      }));
    } catch (error) {
      console.error('Error storing in localStorage:', error);
    }
  };

  const handleAddAccount = async (values) => {
    try {
      // Check if account is already onboarded
      const existingAccount = databricksAccounts.find(
        (account) => account.accountId === values.accountId
      );
      
      if (existingAccount) {
        message.warning('This account is already onboarded');
        return;
      }

      // Prepare payload with only required fields for backend API
      const payload = {
        account_id: values.accountId,
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
        client_id: values.clientId,
        client_secret: values.clientSecret,
      };

      await databricksService.onboardAccount(payload);
      
      // Store onboarding credentials for subsequent Databricks operations
      storeCredentials(values.accountId, {
        client_id: values.clientId,
        client_secret: values.clientSecret,
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
      });

      message.success('Databricks account onboarded successfully');
      setIsAddModalVisible(false);
      addForm.resetFields();
      // Refresh the accounts list to show the newly onboarded account
      await fetchDatabricksAccounts();
      
      // Dispatch custom event to notify dashboard to refresh
      window.dispatchEvent(new Event('databricksAccountsUpdated'));
    } catch (error) {
      const errorMessage = error.message || 'Failed to onboard Databricks account';
      message.error(errorMessage);
      console.error('Onboarding error:', error);
    }
  };

  const toggleHiddenClientId = (accountId, e) => {
    e.stopPropagation();
    if (hiddenClientIds.includes(accountId)) {
      setHiddenClientIds(hiddenClientIds.filter((id) => id !== accountId));
    } else {
      setHiddenClientIds([...hiddenClientIds, accountId]);
    }
  };

  const validateUUID = (_, value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!value) {
      return Promise.reject(new Error('Please enter a valid UUID'));
    }
    if (!uuidRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid UUID'));
    }
    return Promise.resolve();
  };

  const columns = [
    {
      title: 'SI',
      key: 'si',
      width: 80,
      render: (_, record, index) => (
        <span style={{ fontWeight: '500', color: '#666' }}>
          {String(index + 1).padStart(2, '0')}
        </span>
      ),
    },
    {
      title: 'Account ID',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 250,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#333' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Account URL',
      dataIndex: 'accountUrl',
      key: 'accountUrl',
      width: 300,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', color: '#666', wordBreak: 'break-all' }}>
          {text || 'N/A'}
        </span>
      ),
      width: 250,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#333' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Account URL',
      dataIndex: 'accountUrl',
      key: 'accountUrl',
      width: 300,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', color: '#666', wordBreak: 'break-all' }}>
          {text || 'N/A'}
        </span>
      ),
      width: 250,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#333' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Account URL',
      dataIndex: 'accountUrl',
      key: 'accountUrl',
      width: 300,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', color: '#666', wordBreak: 'break-all' }}>
          {text || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
      width: 250,
      width: 250,
      width: 250,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontFamily: 'monospace', color: '#666' }}>
            {hiddenClientIds.includes(record.accountId)
              ? text
              : '*'.repeat(text?.length || 0)}
          </span>
          <Tooltip title={hiddenClientIds.includes(record.accountId) ? "Hide Client ID" : "Show Client ID"}>
            <Button
              type="text"
              style={{
                cursor: 'pointer',
                color: '#888888',
                fontSize: '14px'
              }}
              onClick={(e) => toggleHiddenClientId(record.accountId, e)}
            >
              {hiddenClientIds.includes(record.accountId) ? 'Show' : 'Hide'}
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (text) => (
        <span style={{ color: '#666', fontSize: '13px' }}>
          {text ? new Date(text).toLocaleString() : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (text) => (
        <span style={{ color: '#666', fontSize: '13px' }}>
          {text ? new Date(text).toLocaleString() : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (text) => (
        <span style={{ color: '#666', fontSize: '13px' }}>
          {text ? new Date(text).toLocaleString() : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag
          style={{
            background: status === 'Connected' ? '#16a34a' : '#ff4d4f',
            color: '#fff',
            border: '1px solid',
            borderColor: status === 'Connected' ? '#16a34a' : '#ff4d4f',
            borderRadius: '8px',
            fontWeight: 500,
            padding: '4px 16px',
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              style={{
                color: '#ff4d4f',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fff2f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
              onClick={(e) => {
                e.stopPropagation();
                message.info('Delete functionality coming soon');
              }}
            >
              Delete
            </Button>
          </Tooltip>
          <Tooltip title="Configure Logs">
            <Button
              type="primary"
              size="small"onClick={() => navigate(`/logs?accountId=databricks_${record.accountId}`)}
              style={{
                cursor: 'pointer',
                fontSize: '16px',
              }}
            />
          </Tooltip>
          <Tooltip title="Configure Metrics">
            <Button
              type="primary"
              size="small"onClick={() => navigate(`/databricks-metrics-onboarding?accountId=databricks_${record.accountId}`)}
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                backgroundColor: '#ffffff',
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <Breadcrumb>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>Databricks Accounts</Breadcrumb.Item>
        </Breadcrumb>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button onClick={() => navigate('/accounts')}>
            View AWS Accounts
          </Button>
          <Button onClick={() => fetchDatabricksAccounts()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button onClick={() => setShowRawResponse(!showRawResponse)}>
            {showRawResponse ? 'Hide' : 'Show'} Raw API Response
          </Button>
          <Button onClick={() => fetchDatabricksAccounts()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button onClick={() => setShowRawResponse(!showRawResponse)}>
            {showRawResponse ? 'Hide' : 'Show'} Raw API Response
          </Button>
          <Button onClick={() => fetchDatabricksAccounts()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button onClick={() => setShowRawResponse(!showRawResponse)}>
            {showRawResponse ? 'Hide' : 'Show'} Raw API Response
          </Button>
          <Button type="primary" onClick={() => setIsAddModalVisible(true)} disabled={isViewer}>
            Add Databricks Account
            Add Databricks Account
            Add Databricks Account
          </Button>
        </div>
      </div>
      <h2 style={{ marginBottom: '20px' }}>Databricks Accounts</h2>

      <Spin spinning={loading}>
        <div style={{
          border: '1px solid #f0f0f0',
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#fff'
        }}>
          <Table
            className="custom-ant-table rounded-rows-table"
            columns={columns}
            dataSource={databricksAccounts}
            loading={loading}
            rowKey="accountId"
            pagination={false}
          />
        </div>
      </Spin>

      {/* Raw API Response Debug Section */}
      {showRawResponse && rawApiResponse && (
        <div style={{
          marginTop: '20px',
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          padding: '16px',
          background: '#fafafa'
        }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
            Raw API Response (GET /api/v1/databricks/accounts)
          </h3>
          <pre style={{
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            padding: '12px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {JSON.stringify(rawApiResponse, null, 2)}
          </pre>
        </div>
      )}

      {/* Raw API Response Debug Section */}
      {showRawResponse && rawApiResponse && (
        <div style={{
          marginTop: '20px',
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          padding: '16px',
          background: '#fafafa'
        }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
            Raw API Response (GET /api/v1/databricks/accounts)
          </h3>
          <pre style={{
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            padding: '12px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {JSON.stringify(rawApiResponse, null, 2)}
          </pre>
        </div>
      )}

      {/* Raw API Response Debug Section */}
      {showRawResponse && rawApiResponse && (
        <div style={{
          marginTop: '20px',
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          padding: '16px',
          background: '#fafafa'
        }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
            Raw API Response (GET /api/v1/databricks/accounts)
          </h3>
          <pre style={{
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            padding: '12px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {JSON.stringify(rawApiResponse, null, 2)}
          </pre>
        </div>
      )}

      {/* Configure Logs Modal */}
      <Modal
        title="Configure Logs"
        visible={isConfigureModalVisible}
        onCancel={() => {
          setIsConfigureModalVisible(false);
          form.resetFields();
          setWorkspaces([]);
          setClusterLogPaths([]);
        }}
        onOk={() => {
          form.submit();
        }}
        okText="OK"
        cancelText="Cancel"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveConfiguration}
        >
          <Form.Item
            name="awsAccount"
            label="AWS Account"
            rules={[{ required: true, message: 'Please select an AWS account' }]}
          >
            <Select placeholder="Select AWS account">
              {awsAccounts.map((acc) => (
                <Option key={acc.accountId} value={`${acc.aws_account_name} (${acc.accountId})`}>
                  {acc.aws_account_name} ({acc.accountId})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="workspace"
            label="Workspace Selection"
            rules={[{ required: true, message: 'Please select a workspace' }]}
          >
            <Select
              placeholder="Select workspace"
              onChange={handleWorkspaceChange}
            >
              {workspaces.map((ws) => (
                <Option key={ws.workspace_id} value={ws.workspace_id}>
                  {ws.workspace_name || `Workspace ${ws.workspace_id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="patToken"
            label="PAT Token"
            rules={[{ required: true, message: 'Please enter PAT token' }]}
          >
            <Input.Password
              placeholder="Enter PAT token"
              visibilityToggle
            />
          </Form.Item>

          <Form.Item
            name="patToken"
            label="PAT Token"
            rules={[{ required: true, message: 'Please enter PAT token' }]}
          >
            <Input.Password
              placeholder="Enter PAT token"
              visibilityToggle
            />
          </Form.Item>

          <Form.Item 
            label="Log Types"
            required
            rules={[
              {
                validator: (_, value) => {
                  const auditLogs = form.getFieldValue('auditLogs');
                  const realtimeLogs = form.getFieldValue('realtimeLogs');
                  if (!auditLogs && !realtimeLogs) {
                    return Promise.reject(new Error('Please select at least one log type'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Form.Item name="auditLogs" valuePropName="checked" noStyle>
              <Checkbox 
                onChange={(e) => {
                  if (e.target.checked) {
                    // Populate audit log path when checked
                    form.setFieldsValue({ 
                      auditLogsPath: 's3://databricks-logs-observability/audit' 
                    });
                  } else {
                    // Clear audit log path when unchecked
                    form.setFieldsValue({ auditLogsPath: undefined });
                  }
                }}
              >
                Audit Logs
              </Checkbox>
            </Form.Item>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
              Account-level audit events and API calls.
            </div>

            <Form.Item 
              name="realtimeLogs" 
              valuePropName="checked" 
              noStyle 
              style={{ marginTop: '16px' }}
              dependencies={['workspace']}
            >
              <Checkbox 
                onChange={(e) => {
                  if (e.target.checked && form.getFieldValue('workspace')) {
                    // Trigger workspace change to fetch cluster log paths
                    handleWorkspaceChange(form.getFieldValue('workspace'));
                  } else if (!e.target.checked) {
                    // Clear cluster log path when unchecked
                    form.setFieldsValue({ clusterLogsPath: undefined });
                    setClusterLogPaths([]);
                  }
                }}
              >
                Realtime Logs
              </Checkbox>
            </Form.Item>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
              Live cluster and job execution logs.
            </div>
          </Form.Item>

          <Form.Item
            name="auditLogsPath"
            label="Audit Logs Path"
            dependencies={['auditLogs']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (getFieldValue('auditLogs')) {
                    if (!value) {
                      return Promise.reject(new Error('Please enter audit logs path'));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.auditLogs !== currentValues.auditLogs || prevValues.auditLogsPath !== currentValues.auditLogsPath}>
              {({ getFieldValue }) => {
                const auditLogsEnabled = getFieldValue('auditLogs');
                const currentValue = getFieldValue('auditLogsPath');
                return (
                  <Input
                    placeholder="s3://databricks-logs-observability/audit"
                    disabled={!auditLogsEnabled}
                    style={{ 
                      color: auditLogsEnabled && currentValue ? '#000000' : undefined,
                      WebkitTextFillColor: auditLogsEnabled && currentValue ? '#000000' : undefined
                    }}
                  />
                );
              }}
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="clusterLogsPath"
            label="Cluster Logs Path"
            dependencies={['realtimeLogs', 'workspace']}
            rules={[
              { required: true, message: 'Please select cluster log path' },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (getFieldValue('realtimeLogs')) {
                    if (!value) {
                      return Promise.reject(new Error('Please select cluster log path'));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select 
              placeholder="Select cluster log path" 
              disabled={!form.getFieldValue('realtimeLogs') || !form.getFieldValue('workspace')}
              loading={clusterLogPaths.length === 0 && form.getFieldValue('workspace') && form.getFieldValue('realtimeLogs')}
              notFoundContent={
                form.getFieldValue('realtimeLogs') && form.getFieldValue('workspace') && clusterLogPaths.length === 0 
                  ? 'No cluster log paths available. Please check your workspace configuration.' 
                  : form.getFieldValue('realtimeLogs') && !form.getFieldValue('workspace')
                  ? 'Please select a workspace first'
                  : 'No data'
              }
            >
              {clusterLogPaths.map((path, index) => (
                <Option key={index} value={path}>
                  {path}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Databricks Account Modal */}
      <Modal
        title="Add Databricks Account"
        visible={isAddModalVisible}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddAccount}
          requiredMark="optional"
        >
          <Form.Item
            name="accountId"
            label="* Databricks Account ID"
            rules={[
              { required: true, message: 'Please enter Databricks Account ID' },
              { validator: validateUUID }
            ]}
          >
            <Input placeholder="Enter Databricks Account ID (UUID format)" />
          </Form.Item>

          <Form.Item
            name="accountUrl"
            label="* Account URL"
            rules={[
              { required: true, message: 'Please enter Account URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
            initialValue="https://accounts.cloud.databricks.com"
          >
            <Input placeholder="https://accounts.cloud.databricks.com" />
          </Form.Item>

          <Form.Item
            name="clientId"
            label="* OAuth Client ID"
            rules={[{ required: true, message: 'Please enter OAuth Client ID' }]}
          >
            <Input placeholder="Application ID (UUID format)" />
          </Form.Item>

          <Form.Item
            name="clientSecret"
            label="* OAuth Client Secret"
            rules={[{ required: true, message: 'Please enter OAuth Client Secret' }]}
          >
            <Input.Password
              placeholder="Enter OAuth Client Secret"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsAddModalVisible(false);
                addForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Onboard Account
                Onboard Account
                Onboard Account
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DatabricksAccountsPage;
